package data.repository

import data.datasource.remote.GitHubGraphQLRemoteDataSource
import data.graphql.GitHubGraphQLQueries
import domain.model.*
import domain.repository.GitHubInsightsRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.Clock

/**
 * Repository implementation combining REST and GraphQL data sources
 * Provides organization management, caching logic, and enhanced PR insights
 */
class GitHubInsightsRepositoryImpl(
    private val graphqlDataSource: GitHubGraphQLRemoteDataSource,
    private val restApiDataSource: Any? = null // Placeholder for existing REST API if needed
) : GitHubInsightsRepository {
    
    // Cache for organizations and teams
    private var cachedOrganizations: List<OrganizationInsights>? = null
    private var cachedTeams: List<Team>? = null
    private var cachedUserLogin: String? = null
    private var lastCacheUpdate: Long = 0
    private val cacheValidityPeriod = 5 * 60 * 1000L // 5 minutes
    
    override suspend fun getUserOrganizations(): Result<List<OrganizationInsights>> {
        return try {
            // Check cache validity
            if (cachedOrganizations != null && 
                Clock.System.now().toEpochMilliseconds() - lastCacheUpdate < cacheValidityPeriod) {
                return Result.success(cachedOrganizations!!)
            }
            
            // Get GitHub App installations instead of OAuth organizations
            // This ensures we only show organizations where the app is installed
            val installations = graphqlDataSource.getGitHubAppInstallations()
            
            // Convert installations to OrganizationInsights
            val organizations = installations.map { installation ->
                OrganizationInsights(
                    organization = GitHubOrganization(
                        id = installation.account.id.toString(),
                        login = installation.account.login,
                        name = installation.account.login, // Installation API doesn't provide org name
                        description = null,
                        avatarUrl = installation.account.avatar_url,
                        htmlUrl = "https://github.com/${installation.account.login}",
                        location = null,
                        email = null,
                        twitterUsername = null,
                        blog = null,
                        company = null,
                        createdAt = installation.created_at,
                        updatedAt = installation.updated_at,
                        publicRepos = 0, // Will be filled later if needed
                        privateRepos = 0, // Will be filled later if needed
                        totalPrivateRepos = 0, // Will be filled later if needed
                        publicGists = 0,
                        followers = 0,
                        following = 0,
                        type = installation.account.type,
                        membership = null, // GitHub App installations don't provide membership details
                        ssoProvider = null, // Will be filled later if needed
                        requiresSAML = false, // Will be filled later if needed
                        isVerified = false, // Will be filled later if needed
                        pendingInvitations = emptyList()
                    ),
                    ssoStatus = null, // Will be filled later if needed
                    teams = emptyList(), // Will be filled later if needed
                    recentActivity = OrganizationActivity(
                        totalPullRequests = 0,
                        openPullRequests = 0,
                        totalIssues = 0,
                        openIssues = 0,
                        lastActivityAt = null
                    ),
                    accessLevel = OrganizationAccessLevel.MEMBER // App installations imply access
                )
            }
            
            // Update cache
            cachedOrganizations = organizations
            lastCacheUpdate = Clock.System.now().toEpochMilliseconds()
            
            println("✅ Found ${organizations.size} GitHub App installations")
            installations.forEachIndexed { index, installation -> 
                println("  - ${installation.account.login} (installation: ${installation.id})")
            }
            
            Result.success(organizations)
        } catch (e: Exception) {
            println("🚫 Error getting user organizations: ${e.message}")
            Result.failure(e)
        }
    }
    
    override suspend fun getPullRequestsInsights(
        viewMode: PRViewMode,
        organizations: List<String>,
        teams: List<String>,
        maxResults: Int
    ): Result<List<PullRequestInsights>> {
        return try {
            val currentUser = getCurrentUserLogin().getOrNull()
            val userTeams = getUserTeams().getOrNull() ?: emptyList()
            
            // Build search query based on view mode
            val searchQuery = buildSearchQuery(
                viewMode = viewMode,
                currentUser = currentUser,
                organizations = organizations,
                teams = teams,
                userTeams = userTeams
            )
            
            println("🔍 GraphQL Search Query: $searchQuery")
            
            val pullRequests = graphqlDataSource.getPullRequestsInsights(searchQuery, maxResults)
            
            // Enrich PRs with organization context
            val enrichedPRs = coroutineScope {
                pullRequests.map { pr ->
                    async {
                        enrichPRWithOrganizationContext(pr)
                    }
                }.awaitAll()
            }
            
            // Apply additional filtering if needed
            val filteredPRs = applyAdditionalFiltering(enrichedPRs, viewMode, currentUser, userTeams)
            
            Result.success(filteredPRs)
        } catch (e: Exception) {
            println("🚫 Error getting PR insights: ${e.message}")
            Result.failure(e)
        }
    }
    
    override suspend fun getOrganizationDetails(login: String): Result<OrganizationInsights?> {
        return try {
            val orgDetails = graphqlDataSource.getOrganizationDetails(login)
            val enrichedOrg = orgDetails?.let { enrichOrganizationWithSSO(it) }
            Result.success(enrichedOrg)
        } catch (e: Exception) {
            println("🚫 Error getting organization details for $login: ${e.message}")
            Result.failure(e)
        }
    }
    
    override suspend fun getPendingInvitations(): Result<List<OrganizationInvitation>> {
        return try {
            val invitations = graphqlDataSource.getPendingInvitations()
            Result.success(invitations)
        } catch (e: Exception) {
            println("🚫 Error getting pending invitations: ${e.message}")
            Result.failure(e)
        }
    }
    
    override suspend fun getUserTeams(): Result<List<Team>> {
        return try {
            // Check cache
            if (cachedTeams != null && 
                Clock.System.now().toEpochMilliseconds() - lastCacheUpdate < cacheValidityPeriod) {
                return Result.success(cachedTeams!!)
            }
            
            val teams = graphqlDataSource.getUserTeams()
            cachedTeams = teams
            
            Result.success(teams)
        } catch (e: Exception) {
            println("🚫 Error getting user teams: ${e.message}")
            Result.failure(e)
        }
    }
    
    override suspend fun getSSOStatus(organizationLogin: String): Result<SSOAuthStatus?> {
        return try {
            val ssoStatus = graphqlDataSource.getSSOStatus(organizationLogin)
            Result.success(ssoStatus)
        } catch (e: Exception) {
            println("🚫 Error getting SSO status for $organizationLogin: ${e.message}")
            Result.failure(e)
        }
    }
    
    override suspend fun getOrganizationContext(organizationLogin: String): Result<OrganizationContext?> {
        return try {
            // Get organization details
            val orgDetails = getOrganizationDetails(organizationLogin).getOrNull()
            if (orgDetails == null) {
                return Result.success(null)
            }
            
            // Get user teams for this organization
            val allTeams = getUserTeams().getOrNull() ?: emptyList()
            val orgTeams = allTeams.filter { it.organization.login == organizationLogin }
            
            // Check membership status
            val isUserMember = orgDetails.accessLevel in listOf(
                OrganizationAccessLevel.OWNER,
                OrganizationAccessLevel.ADMIN,
                OrganizationAccessLevel.MEMBER
            )
            
            val canViewPrivateRepos = orgDetails.accessLevel in listOf(
                OrganizationAccessLevel.OWNER,
                OrganizationAccessLevel.ADMIN,
                OrganizationAccessLevel.MEMBER
            )
            
            val context = OrganizationContext(
                organization = orgDetails.organization,
                userTeams = orgTeams,
                isUserMember = isUserMember,
                canViewPrivateRepos = canViewPrivateRepos
            )
            
            Result.success(context)
        } catch (e: Exception) {
            println("🚫 Error getting organization context for $organizationLogin: ${e.message}")
            Result.failure(e)
        }
    }
    
    override suspend fun refreshOrganizationCache(): Result<Unit> {
        return try {
            cachedOrganizations = null
            cachedTeams = null
            lastCacheUpdate = 0
            
            // Trigger fresh fetch
            getUserOrganizations()
            getUserTeams()
            
            Result.success(Unit)
        } catch (e: Exception) {
            println("🚫 Error refreshing organization cache: ${e.message}")
            Result.failure(e)
        }
    }
    
    override suspend fun getCurrentUserLogin(): Result<String?> {
        return try {
            // Check cache first
            if (cachedUserLogin != null) {
                return Result.success(cachedUserLogin)
            }
            
            // This would typically come from a user info query or existing auth system
            // For now, we'll extract it from the organizations query
            val organizations = getUserOrganizations().getOrNull()
            if (organizations?.isNotEmpty() == true) {
                // Extract from GraphQL response - would need to modify query to include viewer login
                cachedUserLogin = "current-user" // Placeholder - would be actual login
            }
            
            Result.success(cachedUserLogin)
        } catch (e: Exception) {
            println("🚫 Error getting current user login: ${e.message}")
            Result.failure(e)
        }
    }
    
    // Helper methods
    private suspend fun enrichOrganizationWithSSO(orgInsight: OrganizationInsights): OrganizationInsights {
        return try {
            val ssoStatus = if (orgInsight.organization.requiresSAML) {
                getSSOStatus(orgInsight.organization.login).getOrNull()
            } else {
                null
            }
            
            orgInsight.copy(ssoStatus = ssoStatus)
        } catch (e: Exception) {
            println("⚠️ Could not enrich SSO data for ${orgInsight.organization.login}: ${e.message}")
            orgInsight
        }
    }
    
    private suspend fun enrichPRWithOrganizationContext(pr: PullRequestInsights): PullRequestInsights {
        return try {
            val orgLogin = pr.repository.organization?.login
            if (orgLogin != null) {
                val orgContext = getOrganizationContext(orgLogin).getOrNull()
                
                pr.copy(
                    organization = orgContext?.organization,
                    teams = orgContext?.userTeams ?: emptyList()
                )
            } else {
                pr
            }
        } catch (e: Exception) {
            println("⚠️ Could not enrich PR with organization context: ${e.message}")
            pr
        }
    }
    
    private fun buildSearchQuery(
        viewMode: PRViewMode,
        currentUser: String?,
        organizations: List<String>,
        teams: List<String>,
        userTeams: List<Team>
    ): String {
        return when (viewMode) {
            PRViewMode.ALL_PRS -> {
                GitHubGraphQLQueries.buildPRSearchQuery(
                    organizations = organizations
                )
            }
            
            PRViewMode.ONLY_MINE -> {
                GitHubGraphQLQueries.buildPRSearchQuery(
                    author = currentUser,
                    organizations = organizations
                )
            }
            
            PRViewMode.EVERYTHING_EXCEPT_MINE -> {
                GitHubGraphQLQueries.buildPRSearchQuery(
                    excludeAuthor = currentUser,
                    organizations = organizations
                )
            }
            
            PRViewMode.TEAM_ONLY -> {
                val teamFilters = if (teams.isNotEmpty()) {
                    teams
                } else {
                    // Use user's teams
                    userTeams.map { "${it.organization.login}/${it.slug}" }
                }
                
                GitHubGraphQLQueries.buildPRSearchQuery(
                    teams = teamFilters,
                    organizations = organizations
                )
            }
        }
    }
    
    private fun applyAdditionalFiltering(
        pullRequests: List<PullRequestInsights>,
        viewMode: PRViewMode,
        currentUser: String?,
        userTeams: List<Team>
    ): List<PullRequestInsights> {
        return when (viewMode) {
            PRViewMode.TEAM_ONLY -> {
                // Additional filtering for team PRs if GraphQL search wasn't precise enough
                val userTeamSlugs = userTeams.map { "${it.organization.login}/${it.slug}" }.toSet()
                
                pullRequests.filter { pr ->
                    pr.teams.any { team ->
                        val teamSlug = "${team.organization.login}/${team.slug}"
                        teamSlug in userTeamSlugs
                    }
                }
            }
            
            else -> pullRequests
        }
    }
    
    override suspend fun diagnoseOrganizationAccess(): Result<OrganizationAccessDiagnosis> {
        return try {
            val diagnosis = graphqlDataSource.diagnoseOrganizationAccess()
            Result.success(diagnosis)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
